import React from "react";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { makeStyles } from "@material-ui/core/styles";
import { LightenDarkenColor } from "lighten-darken-color";
import Tally from "./counterStyle.svg";
import {
  List,
  ListItem,
  Pagination,
  InputAdornment,
  Divider,
  Button,
  IconButton,
  Card,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import { Key, Plus, Minus, TriangleAlert, Camera as CameraIcon, Send, Image, SwitchCamera, Circle, X } from "lucide-react";
import Webcam from "react-webcam";
import { parseNewDateToDDMMYYY } from "../../../Utils/dateHelper";

function Counter(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [captured, setCaptured] = useState(false);
  const [extraContent, setExtraContent] = useState(false);
  const [openComment, setOpenComment] = useState(false);
  const [openCamera, setOpenCamera] = useState(false);
  const [openFullscreenImage, setOpenFullscreenImage] = useState(false);
  const [image, setImage] = useState("");
  const [comment, setComment] = useState("");
  const [cameraSide, setCameraSide] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const webcamRef = React.useRef(null);
  const [operator, setOperator] = useState(false);
  const [listOfComments, setListOfComments] = useState([]);
  const [page, setPage] = useState(1);
  const [disableDecrement, setDisableDecrement] = useState(true);

  const titleRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (titleRef.current) {
        const element = titleRef.current;

        // Temporarily remove the clamp styles to measure natural height
        const originalStyle = element.style.webkitLineClamp;
        element.style.webkitLineClamp = "unset";
        element.style.display = "block";

        // Measure natural height
        const naturalHeight = element.getBoundingClientRect().height;

        // Restore original styles
        element.style.webkitLineClamp = originalStyle;
        element.style.display = "-webkit-box";

        // Get line height dynamically
        const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
        const maxHeight = lineHeight * 2; // Two lines

        setIsOverflowing(naturalHeight > maxHeight);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [props.description]);

  const titleElement = (
    <h2 ref={titleRef} className={classes.h2style}>
      {props.description}
    </h2>
  );

  useEffect(() => {
    if (props.counter === "0" && disableDecrement === false) {
      setDisableDecrement(true);
    }

    if (props.counter !== "0" && disableDecrement === true) {
      setDisableDecrement(false);
    }
  }, [props.counter, disableDecrement]);

  useEffect(() => {
    setListOfComments(props.comments);
    // eslint-disable-next-line no-undef
  }, [props.refresh, props.comments]);

  const cameraFace = {
    facingMode: "user",
  };

  const cameraBack = {
    facingMode: { exact: "environment" },
  };

  const handleDialogOpenComment = () => {
    setOpenComment(true);
  };

  const handleDialogCloseComment = () => {
    setOpenComment(false);
  };

  const handleDialogOpenCamera = () => {
    setOpenCamera(true);
    setOpenComment(false);
  };

  const handleDialogCloseCamera = () => {
    setOpenCamera(false);
    setOpenComment(true);
  };

  const handleDialogOpenFullscreenImage = () => {
    setOpenFullscreenImage(true);
  };

  const handleDialogCloseFullscreenImage = () => {
    setOpenFullscreenImage(false);
  };

  const Cameremode = () => {
    setCameraSide(!cameraSide);
  };

  const handleExtraContentClick = () => {
    setExtraContent(!extraContent);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handlePage = (event, value) => {
    setPage(value);
  };

  const Increment = () => {
    if (props.comment) {
      setComment("");
      setImage("");
      handleDialogOpenComment();
      setOperator(true);
    } else {
      props.action(parseInt(props.counter) + 1, props.id, false);
    }
  };

  const Decrement = () => {
    if (props.comment) {
      setComment("");
      setImage("");
      handleDialogOpenComment();
      setOperator(false);
    } else {
      props.action(parseInt(props.counter) - 1, props.id, true);
    }
  };

  const handleSendWithComment = () => {
    if (operator) {
      props.actionWithComment(parseInt(props.counter) + 1, props.id, image.slice(23), comment);
    } else {
      props.actionWithComment(parseInt(props.counter) - 1, props.id, image.slice(23), comment);
    }
    handleDialogCloseComment();
    let tempListOfComments = [...listOfComments];
    let index = tempListOfComments.length + 1;
    let newListItem = { image: image, comment: comment, index: index++, insertedOn: new Date(Date.now()) };
    tempListOfComments.push(newListItem);
    setListOfComments(tempListOfComments);
    setPage(index - 1);
  };

  const capture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc !== null) {
      setCaptured(true);
      setImage(imageSrc);
    } else {
      alert("No Camera Found");
    }
  };

  const CameraStyle = {
    width: "100%",
    height: "100%",
    position: "absolute",
    objectFit: "cover",
    top: "0",
    left: "0",
  };

  return (
    <div>
      <Card>
        <div
          className={classes.StyleCounter}
          style={{
            color: props.textColor,
            backgroundColor: `${props.color}`,
          }}
        >
          <div className={classes.headerDiv}>
            {props.isKey && <Key className={classes.keyStyle}></Key>}
            {props.comment && (listOfComments.length > 0 || listOfComments !== "") ? (
              <Button style={{ zIndex: "4", position: "absolute", left: "3%" }} size={"large"} onClick={handleExtraContentClick}>
                <TriangleAlert style={{ color: props.textColor }} />
              </Button>
            ) : (
              <React.Fragment></React.Fragment>
            )}
            {isOverflowing ? (
              <Tooltip title={props.description} placement="top" arrow>
                {titleElement}
              </Tooltip>
            ) : (
              titleElement
            )}
            <img className={classes.imageStyle} style={{ filter: props.darkness ? "invert(1)" : "contrast(80%)" }} src={Tally} alt="" />
          </div>
          <Divider></Divider>
          <div className={classes.counterDiv} style={{ backgroundColor: LightenDarkenColor(props.color, 20) }}>
            <p style={{ fontSize: "50px" }}>{props.counter}</p>
          </div>
          <Divider></Divider>
          <div className={classes.StyleDiv}>
            <Button style={{ color: props.textColor, width: "100%", height: "100%", cursor: disableDecrement ? "not-allowed" : "pointer" }} onClick={Decrement} disabled={disableDecrement}>
              <Minus fontSize="large" style={{ color: props.textColor, width: "100%" }} />
            </Button>
            <Divider orientation="vertical"></Divider>
            <Button style={{ color: props.textColor, width: "100%", height: "100%" }} onClick={Increment}>
              <Plus fontSize="large" style={{ color: props.textColor, width: "100%" }} />
            </Button>
          </div>
        </div>

        <Dialog fullScreen open={openCamera} onClose={handleDialogCloseCamera} maxWidth={"lg"}>
          <DialogTitle style={{ background: "rgba(0, 0, 0, .25)", zIndex: "1", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3>{t('performMeasurement.camera')}</h3>
            <IconButton onClick={handleDialogCloseCamera} size="large">
              <X style={{ color: "white", width: "40px", height: "40px" }} />
            </IconButton>
          </DialogTitle>
          <Divider></Divider>
          <DialogContent>
            {cameraSide === true ? (
              <Webcam style={CameraStyle} audio={false} ref={webcamRef} screenshotFormat="image/jpeg" videoConstraints={cameraBack}></Webcam>
            ) : (
              <Webcam style={CameraStyle} audio={false} ref={webcamRef} screenshotFormat="image/jpeg" videoConstraints={cameraFace}></Webcam>
            )}
          </DialogContent>
          <Divider></Divider>
          <DialogActions style={{ justifyContent: "center", background: "rgba(0, 0, 0, .25)", zIndex: "1" }}>
            <IconButton onClick={handleExpandClick} size="large">
              {captured && image !== "" ? (
                <img alt="afbeelding" style={{ margin: "0px", width: "2rem", height: "2rem", borderRadius: "50%" }} src={image}></img>
              ) : (
                <Image fontSize="inherit" style={{ color: "white", width: "40px", height: "40px" }} />
              )}
            </IconButton>
            <IconButton onClick={capture} size="large">
              <Circle fontSize="inherit" style={{ color: "white", width: "40px", height: "40px" }} />
            </IconButton>
            <IconButton onClick={Cameremode} size="large">
              <SwitchCamera fontSize="inherit" style={{ color: "white", width: "40px", height: "40px" }} />
            </IconButton>
          </DialogActions>
          <Divider style={{ background: "rgb(0, 0, 0)", zIndex: "2" }}></Divider>
          <Collapse style={{ background: "rgb(255, 255, 255)", zIndex: "2" }} in={expanded} timeout="auto" unmountOnExit>
            <DialogContent style={{ background: "rgb(255, 255, 255)", zIndex: "2" }}>
              <img alt="afbeelding" style={{ background: "rgb(255, 255, 255)", zIndex: "2", maxWidth: "100%", maxHeight: "calc(100vh - 64px)" }} src={image}></img>
            </DialogContent>
          </Collapse>
        </Dialog>

        <Dialog fullWidth open={openComment} onClose={handleDialogCloseComment}>
          <DialogTitle>
            {t('performMeasurement.comment')}
            <IconButton style={{ position: "absolute", right: "10px", top: "5px" }} onClick={handleDialogCloseComment} size="large">
              <X fontSize="inherit" color="blue" />
            </IconButton>
          </DialogTitle>
          <Divider></Divider>
          <div className={classes.StyleDivComment}>
            <TextField
              multiline
              rows={8}
              style={{ marginBottom: "10px", margin: "10px", width: "87%" }}
              label={comment ? " " : t('performMeasurement.makeAComment')}
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              onChange={(event) => setComment(event.target.value)}
              value={comment}
              InputProps={{
                endAdornment: (
                  <InputAdornment style={{ marginTop: "160px" }}>
                    {image !== "" ? (
                      <div>
                        <IconButton onClick={handleDialogOpenFullscreenImage}>
                          <img alt="afbeelding" style={{ margin: "0px", width: "2rem", height: "2rem", borderRadius: "50%" }} src={image}></img>
                        </IconButton>
                        <IconButton onClick={handleDialogOpenCamera}>
                          <CameraIcon />
                        </IconButton>
                      </div>
                    ) : (
                      <div>
                        <IconButton onClick={handleDialogOpenCamera}>
                          <CameraIcon />
                        </IconButton>
                      </div>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <IconButton style={{ marginBottom: "12px" }} onClick={handleSendWithComment} size="large">
              <Send fontSize="inherit" color="blue" />
            </IconButton>
          </div>
        </Dialog>

        <Dialog fullScreen open={openFullscreenImage} onClose={handleDialogCloseFullscreenImage}>
          <DialogTitle style={{ background: "rgba(0, 0, 0, .25)", zIndex: "1", display: "flex", justifyContent: "end" }}>
            <IconButton style={{}} onClick={handleDialogCloseFullscreenImage} size="large">
              <X fontSize="inherit" color="white" style={{ width: "40px", height: "40px" }} />
            </IconButton>
          </DialogTitle>
          <Divider></Divider>
          <DialogContent>
            <img alt="afbeelding" style={CameraStyle} src={image}></img>
          </DialogContent>
        </Dialog>
      </Card>
      {extraContent ? (
        <div style={{ position: "relative" }}>
          <div style={{ position: "absolute", top: "1", width: "100%", zIndex: "10" }}>
            <Collapse in={extraContent} timeout="auto" unmountOnExit>
              <Card>
                {listOfComments
                  .filter((list) => list.index === page)
                  .map((x) => (
                    <List sx={{ width: "100%", overflow: "hidden" }}>
                      <ListItem>
                        <Typography style={{ width: "70%" }}>{t('performMeasurement.commentIndex', { description: props.description, index: x.index })}</Typography>
                        <Typography style={{ width: "30%", fontWeight: "bold" }}>{parseNewDateToDDMMYYY(new Date(x.insertedOn))}</Typography>
                      </ListItem>
                      <Divider></Divider>
                      {x.image !== "" && x.image !== null ? (
                        <ListItem style={{ marginBottom: "10px", display: "flex", justifyContent: "center" }}>
                          <img alt="afbeelding" src={x.image} style={{ maxWidth: "100%", maxHeight: "calc(40vh - 16px)", borderRadius: "15px" }} />
                        </ListItem>
                      ) : (
                        <React.Fragment></React.Fragment>
                      )}
                      {x.comment !== "" ? (
                        <ListItem>
                          <Typography style={{ hyphens: "auto", wordWrap: "break-word", overflowWrap: "anywhere" }}>{x.comment}</Typography>
                        </ListItem>
                      ) : (
                        <React.Fragment></React.Fragment>
                      )}
                      {x.comment === "" && (x.image === "" || x.image === null) ? (
                        <ListItem>
                          <Typography style={{ color: "red" }}>{t('performMeasurement.noRecordedComment')}</Typography>
                        </ListItem>
                      ) : (
                        <React.Fragment></React.Fragment>
                      )}
                      <Divider></Divider>
                      <ListItem style={{ justifyContent: "center" }}>
                        <Pagination page={page} variant="outlined" size="small" siblingCount={0} count={listOfComments.length} onChange={handlePage} />
                      </ListItem>
                    </List>
                  ))}
              </Card>
            </Collapse>
          </div>
        </div>
      ) : (
        <React.Fragment></React.Fragment>
      )}
    </div>
  );
}
export default Counter;

const useStyles = makeStyles({
  headerDiv: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "33%",
    position: "relative",
  },
  h2style: {
    margin: "0",
    zIndex: "2",
    maxWidth: "calc(100% - 150px)",

    // break words if they are too long
    whiteSpace: "normal",
    textOverflow: "ellipsis",
    wordWrap: "break-word",

    // max two lines
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  StyleDiv: {
    height: "33%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
    alignItems: "center",
  },
  StyleDivComment: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
  },
  counterDiv: {
    display: "flex",
    justifyContent: "center",
    height: "34%",
    alignItems: "center",
  },
  fabStyle: {
    position: "absolute",
    left: "3%",
  },
  imageStyle: {
    position: "absolute",
    right: "3%",
    opacity: "50%",
    width: "50px",
  },
  keyStyle: {
    position: "absolute",
    left: "3%",
    opacity: "50%",
    width: "50px",
  },
  StyleCounter: {
    overflow: "hidden",
    backgroundRepeat: "no-repeat !important",
    backgroundSize: "10rem 10rem",
    backgroundPosition: "center",
    height: "12rem",
  },
});
